import React, { useContext, useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars } from '@fortawesome/free-solid-svg-icons'
import AuthContext from '../../middlewares/AuthContext'
import Swal from 'sweetalert2'
import { useNavigate, Link } from 'react-router-dom'
import jwt_decode from 'jwt-decode'
import { useTranslation } from 'react-i18next'

// ── Types ─────────────────────────────────────────────────────────────────────
interface AuthCtx {
  token: string | null
  onLogout: () => void
  getHeaders: () => Promise<Record<string, string>>
}

interface Props {
  showNavbar: (toggleId: string, navId: string, bodyId: string, headerId: string) => void
  flip: boolean
}

// ── Role badge colors ─────────────────────────────────────────────────────────
const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  admin      : { bg: '#dc3545', text: '#fff' },
  partner    : { bg: '#0d6efd', text: '#fff' },
  subpartner : { bg: '#6f42c1', text: '#fff' },
  customer   : { bg: '#198754', text: '#fff' },
  marketing  : { bg: '#fd7e14', text: '#fff' },
}

// ── Header ────────────────────────────────────────────────────────────────────
const Header: React.FC<Props> = ({ showNavbar, flip }) => {
  const { onLogout, token, getHeaders } = useContext(AuthContext) as AuthCtx
  const { t, i18n }                     = useTranslation()
  const navigate                        = useNavigate()
  const tokenDecode: any                = token !== null ? jwt_decode(token) : null

  const [name, setName]     = useState('')
  const [role, setRole]     = useState('')

  const logout = () => {
    Swal.fire({
      title             : t('header.logout_session'),
      showCancelButton  : true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor : '#d33',
      confirmButtonText : t('header.yes_logout_session'),
      showClass : { popup: 'animate__animated animate__fadeInDown' },
      hideClass : { popup: 'animate__animated animate__fadeOutUp' },
    }).then(result => {
      if (result.isConfirmed) {
        onLogout()
        navigate('/')
      }
    })
  }

  useEffect(() => {
    document.querySelector<HTMLElement>('#header-toggle')?.click()
  }, [])

  useEffect(() => {
    buildName()
  }, [i18n.language])

  const buildName = () => {
    const roleName = tokenDecode?.roles?.[0]?.name ?? ''
    setRole(roleName)
    switch (roleName) {
      case 'admin':
        setName(t('header.administrator'))
        break
      case 'customer':
        setName('Portal LD')
        break
      case 'marketing': {
        const mar = tokenDecode?.marketing?.name
        setName(mar ? `Marketing – ${mar}` : 'Marketing')
        break
      }
      case 'partner': {
        const par = tokenDecode?.partner?.name
        setName(par ? `${t('header.partner')} – ${par}` : t('header.partner'))
        break
      }
      case 'subpartner': {
        const sub = tokenDecode?.partner?.name
        setName(sub ? `S – ${sub}` : 'Subpartner')
        break
      }
      default:
        setName('Portal LD')
    }
  }

  const roleStyle = ROLE_COLORS[role] ?? { bg: '#6eb446', text: '#fff' }

  return (
    <header
      className="header shadow-sm"
      id="header"
      style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}
    >
      {/* Left — hamburger + name */}
      <div className="header_toggle d-flex align-items-center gap-2">
        <div
          id="header-toggle"
          onClick={() => showNavbar('header-toggle', 'nav-bar', 'body-pd', 'header')}
          style={{ cursor: 'pointer', color: 'var(--first-color)', fontSize: '1.25rem', lineHeight: 1 }}
        >
          <FontAwesomeIcon icon={faBars} />
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold text-capitalize" style={{ fontSize: '0.9rem' }}>
            {name}
          </span>
          {role && (
            <span
              style={{
                fontSize: '0.68rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 9999,
                backgroundColor: roleStyle.bg,
                color: roleStyle.text,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {role}
            </span>
          )}
        </div>
      </div>

      {/* Right — user dropdown */}
      <div className="dropdown">
        <button
          className="btn btn-sm d-flex align-items-center gap-2 border"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-auto-close="outside"
          aria-expanded="false"
          style={{
            borderRadius: 9999,
            borderColor: '#e2e8f0',
            backgroundColor: 'transparent',
            fontSize: '0.82rem',
            fontWeight: 600,
            maxWidth: 200,
          }}
        >
          <span
            style={{
              width: 28, height: 28, borderRadius: '50%',
              backgroundColor: '#6eb446',
              color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
            }}
          >
            {tokenDecode?.email?.[0]?.toUpperCase() ?? '?'}
          </span>
          <span
            className="text-truncate d-none d-sm-inline"
            style={{ maxWidth: 140 }}
          >
            {tokenDecode?.email}
          </span>
        </button>

        <ul className="dropdown-menu dropdown-menu-end shadow-sm" style={{ minWidth: 200, borderRadius: 12, border: '1px solid #f1f5f9' }}>

          {/* Email (mobile) */}
          <li className="d-sm-none">
            <span className="dropdown-item-text text-muted" style={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>
              {tokenDecode?.email}
            </span>
            <hr className="dropdown-divider my-1" />
          </li>

          {/* Main portal */}
          <li>
            <Link className="dropdown-item d-flex align-items-center gap-2" to="/" style={{ fontSize: '0.85rem' }}>
              <span>🏠</span> {t('header.main_portal')}
            </Link>
          </li>

          <li><hr className="dropdown-divider my-1" /></li>

          {/* Language */}
          <li>
            <div className="btn-group dropstart w-100">
              <button
                className="dropdown-item d-flex align-items-center gap-2"
                type="button"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                aria-expanded="false"
                style={{ fontSize: '0.85rem' }}
              >
                {i18n.language === 'en'
                  ? <span className="fi fi-us" />
                  : <span className="fi fi-mx" />
                }
                <span>{t('header.language')}</span>
                <span className="ms-auto" style={{ fontSize: '0.7rem', color: '#94a3b8' }}>›</span>
              </button>
              <ul className="dropdown-menu" style={{ borderRadius: 10, border: '1px solid #f1f5f9' }}>
                <li>
                  <button className="dropdown-item d-flex align-items-center gap-2" type="button" onClick={() => i18n.changeLanguage('en')} style={{ fontSize: '0.85rem' }}>
                    <span className="fi fi-us" /> English
                  </button>
                </li>
                <li>
                  <button className="dropdown-item d-flex align-items-center gap-2" type="button" onClick={() => i18n.changeLanguage('es')} style={{ fontSize: '0.85rem' }}>
                    <span className="fi fi-mx" /> Español
                  </button>
                </li>
              </ul>
            </div>
          </li>

          <li><hr className="dropdown-divider my-1" /></li>

          {/* Logout */}
          <li>
            <button
              className="dropdown-item d-flex align-items-center gap-2"
              type="button"
              onClick={logout}
              style={{ fontSize: '0.85rem', color: '#dc3545', marginLeft: '.4rem' }}
            >
              {t('header.logout')}
            </button>
          </li>
        </ul>
      </div>
    </header>
  )
}

export default Header
